import React, { useState } from 'react';
import { SparklesIcon, XMarkIcon, CheckIcon } from 'components/icons';
import { userApi } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';

interface InferredPrefs {
  topNaicsCodes: { code: string; count: number; confidence: number }[];
  topAgencies: { name: string; count: number; confidence: number }[];
  topSetAsides: { code: string; count: number; confidence: number }[];
  interactionCount: number;
}

interface PreferenceConfirmationProps {
  inferredPrefs: InferredPrefs;
  onConfirmed?: () => void;
  onDismissed?: () => void;
}

const DISMISS_KEY = 'pref_confirm_dismissed';
const DISMISS_DAYS = 7;

const PreferenceConfirmation: React.FC<PreferenceConfirmationProps> = ({
  inferredPrefs,
  onConfirmed,
  onDismissed,
}) => {
  const { refreshUser } = useAuth();
  const [visible, setVisible] = useState(() => {
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000) return false;
    }
    return true;
  });
  const [confirming, setConfirming] = useState(false);

  if (!visible || inferredPrefs.interactionCount < 3) return null;

  // Build description from top preferences
  const topAgency = inferredPrefs.topAgencies?.[0]?.name;
  const topNaics = inferredPrefs.topNaicsCodes?.[0]?.code;
  const topSetAside = inferredPrefs.topSetAsides?.[0]?.code;

  if (!topAgency && !topNaics) return null;

  const buildDescription = () => {
    const parts: string[] = [];
    if (topNaics) parts.push(`NAICS ${topNaics}`);
    if (topAgency) parts.push(topAgency);
    if (topSetAside) parts.push(`${topSetAside} set-aside`);
    return parts.join(' / ');
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const data: any = {};
      if (inferredPrefs.topNaicsCodes?.length > 0) {
        data.naicsCodes = inferredPrefs.topNaicsCodes.slice(0, 3).map(n => n.code);
      }
      if (inferredPrefs.topAgencies?.length > 0) {
        data.agencies = inferredPrefs.topAgencies.slice(0, 3).map(a => a.name);
      }
      if (inferredPrefs.topSetAsides?.length > 0) {
        data.setAsides = inferredPrefs.topSetAsides.slice(0, 2).map(s => s.code);
      }

      await userApi.confirmPreferences(data);
      await refreshUser();
      toast.success('Preferences saved! Your results will improve.');
      setVisible(false);
      onConfirmed?.();
    } catch {
      toast.error('Failed to save preferences');
    } finally {
      setConfirming(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setVisible(false);
    onDismissed?.();
  };

  return (
    <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <SparklesIcon className="h-5 w-5 text-brand-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-content">
            Clevor noticed a pattern in your browsing
          </p>
          <p className="text-sm text-content-muted mt-1">
            You've been looking at <strong>{buildDescription()}</strong> contracts. Want us to prioritize these?
          </p>
          <div className="flex items-center gap-3 mt-3">
            <button
              onClick={handleConfirm}
              disabled={confirming}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-500 disabled:opacity-50"
            >
              <CheckIcon className="h-4 w-4" />
              {confirming ? 'Saving...' : 'Yes, focus on these'}
            </button>
            <button
              onClick={handleDismiss}
              className="text-sm text-content-muted hover:text-content"
            >
              Not really
            </button>
          </div>
        </div>
        <button onClick={handleDismiss} className="text-content-subtle hover:text-content-muted flex-shrink-0">
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default PreferenceConfirmation;
